import axiosInstance from '@/core/api'
import {
	MessageConditionSingleResponse,
	MessageConditionAutoResponse,
	GetPlayerByConditionRequest,
	CreateMessageTransactionRequest,
	GetMessageResponse,
	PlayerForMessagingModel,
	GetSmsProvidersResponse,
	GetSmsProviderSendersResponse,
	GetMessageTransactionHistoryRequest,
	GetMessageTransactionHistoryResponse,
	GetChannelTransactionsRequest,
	GetChannelTransactionsResponse,
	GetAutoMessageResponse,
	UpdateAutoMessageRequest,
	UpdateAutoMessageResponse,
	IntervalMessageTransactionModel,
	GetIntervalMessagesResponse,
	CreateIntervalMessageDto,
	UpdateIntervalMessageDto,
	GetMessagingAnalyticsParams,
	MessagingAnalyticsResponse,
	FlexMessageAnalyticsListResponse,
} from '../../types'

class MessagingService {
	// Condition endpoints
	async getMessageConditionsSingle() {
		const response = await axiosInstance.get<MessageConditionSingleResponse>(
			'/admin/message/conditions/single',
		)
		return response.data
	}

	async getMessageConditionsAuto() {
		const response = await axiosInstance.get<MessageConditionAutoResponse>(
			'/admin/message/conditions/auto',
		)
		return response.data
	}

	// Player grouping
	async groupPlayersByCondition(
		data: GetPlayerByConditionRequest,
	): Promise<PlayerForMessagingModel[]> {
		const response = await axiosInstance.post<PlayerForMessagingModel[]>(
			'/admin/message/group-player-by-condition',
			data,
		)
		return response.data
	}

	// Message transaction endpoints
	async createMessageTransaction(data: CreateMessageTransactionRequest) {
		const response = await axiosInstance.post<GetMessageResponse>(
			'/admin/message/create-message',
			data,
		)
		return response.data
	}

	async getMessageTransactionHistory(
		payload: GetMessageTransactionHistoryRequest,
	) {
		const response =
			await axiosInstance.post<GetMessageTransactionHistoryResponse>(
				'/admin/message/transaction-history',
				payload,
			)
		return response.data
	}

	// Channel transactions
	async getChannelTransactions(payload: GetChannelTransactionsRequest) {
		const response = await axiosInstance.post<GetChannelTransactionsResponse>(
			'/admin/message/channel-transactions',
			payload,
		)
		return response.data
	}

	// Auto message endpoints
	async createAutoMessageTransaction(data: CreateMessageTransactionRequest) {
		const response = await axiosInstance.post<GetAutoMessageResponse>(
			'/admin/message/create-auto-message',
			data,
		)
		return response.data
	}

	async getAllAutoMessages() {
		const response = await axiosInstance.get<GetAutoMessageResponse[]>(
			'/admin/message/auto-message-all',
		)
		return response.data
	}

	async getAutoMessageById(id: string) {
		const response = await axiosInstance.get<GetAutoMessageResponse>(
			`/admin/message/auto-message-by-id/${id}`,
		)
		return response.data
	}

	async updateAutoMessage(id: string, data: UpdateAutoMessageRequest) {
		const response = await axiosInstance.put<UpdateAutoMessageResponse>(
			`/admin/message/auto-message-update/${id}`,
			data,
		)
		return response.data
	}

	async deleteAutoMessage(id: string) {
		const response = await axiosInstance.delete(
			`/admin/message/auto-message-delete/${id}`,
		)
		return response.data
	}

	// Interval message endpoints
	async getIntervalMessages() {
		const response = await axiosInstance.get<GetIntervalMessagesResponse>(
			'/admin/interval-message',
		)
		return response.data
	}

	async getIntervalMessageById(id: string) {
		const response = await axiosInstance.get<IntervalMessageTransactionModel>(
			`/admin/interval-message/${id}`,
		)
		return response.data
	}

	async createIntervalMessage(data: CreateIntervalMessageDto) {
		const response = await axiosInstance.post<IntervalMessageTransactionModel>(
			'/admin/interval-message',
			data,
		)
		return response.data
	}

	async updateIntervalMessage(id: string, data: UpdateIntervalMessageDto) {
		const response = await axiosInstance.patch<IntervalMessageTransactionModel>(
			`/admin/interval-message/${id}`,
			data,
		)
		return response.data
	}

	async deleteIntervalMessage(id: string) {
		const response = await axiosInstance.delete(`/admin/interval-message/${id}`)
		return response.data
	}

	// SMS provider endpoints
	async getSMSProviders() {
		const response = await axiosInstance.get<GetSmsProvidersResponse>(
			'/admin/message/providers',
		)
		return response.data
	}

	async getSMSProviderSenders(providerId: string) {
		const response = await axiosInstance.get<GetSmsProviderSendersResponse>(
			`/admin/message/providers/${providerId}/senders`,
		)
		return response.data
	}

	// Analytics endpoints
	async getMessagingAnalytics(params: GetMessagingAnalyticsParams) {
		const response = await axiosInstance.get<MessagingAnalyticsResponse>(
			'/admin/message/analytics',
			{ params },
		)
		return response.data
	}

	// Flex message analytics endpoints
	async getFlexMessageAnalytics() {
		const response = await axiosInstance.get<FlexMessageAnalyticsListResponse>(
			'/admin/message/flex-analytics',
		)
		return response.data
	}
}

export default new MessagingService()
export { default as BrokeFlexMessageService } from './brokeFlexMessage'

export const previewBrokeFlexMessageStage = (
	campaignId: string,
	stageId: string,
) => {
	return axiosInstance.post(
		`/admin/broke-flex-messages/campaigns/${campaignId}/preview/${stageId}`,
	)
}
